import {
  Box,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Text,
  Code,
  Flex,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState, useCallback } from 'react';

import { error as commonError } from '@finley-technologies/common';

import {
  GenerateOmniEmbedUrlQueryGQLType,
  useGenerateOmniEmbedUrlLazyQueryGQLType,
} from '../requests/omni.gql.generated';

type OmniEmbedContext = NonNullable<
  GenerateOmniEmbedUrlQueryGQLType['response']['embedContext']
>;

type OmniEmbedContainerProps = {
  organizationId: string;
  height?: string;
};

/**
 * Omni embed container component.
 * Backend controls all embed options (mode, roles, etc.)
 */
export const OmniEmbedContainer = ({
  organizationId,
  height = '800px',
}: OmniEmbedContainerProps) => {
  const toast = useToast();
  // Store the current embed URL to display and use
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Internal-only diagnostics returned by the backend (null for customers).
  const [embedContext, setEmbedContext] = useState<OmniEmbedContext | null>(
    null,
  );

  const [fetchEmbedUrl] = useGenerateOmniEmbedUrlLazyQueryGQLType({
    fetchPolicy: 'network-only',
  });

  // Fetch a fresh embed URL whenever organizationId or mode changes
  const loadEmbedUrl = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setEmbedUrl(null);
    setEmbedContext(null);

    try {
      const result = await fetchEmbedUrl({
        variables: { organizationId },
      });

      if (result.error) {
        setErrorMessage(result.error.message);
        Sentry.captureException(
          new commonError.FinleyError(
            `Failed to generate Omni embed URL [error:${result.error.message}]`,
            { metadata: { logMeta: { error: result.error, organizationId } } },
          ),
        );
        toast({
          title:
            'An error occurred while loading Analytics. Please contact support@finleycms.com if this continues.',
          status: 'error',
          position: 'top',
          duration: 6000,
          isClosable: true,
        });
      } else if (result.data?.response.error) {
        setErrorMessage(result.data.response.error);
        Sentry.captureException(
          new commonError.FinleyError(
            `Omni embed URL generation error [error:${result.data.response.error}]`,
            { metadata: { logMeta: { organizationId } } },
          ),
        );
      } else if (result.data?.response.url) {
        setEmbedUrl(result.data.response.url);
        setEmbedContext(result.data.response.embedContext ?? null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, fetchEmbedUrl, toast]);

  useEffect(() => {
    loadEmbedUrl();
  }, [loadEmbedUrl]);

  if (isLoading) {
    return (
      <Box>
        <Flex mb={2} fontSize="sm" color="gray.500" gap={2}>
          <Text>Loading Analytics…</Text>
        </Flex>
        <Skeleton height={height} data-testid="omni-embed-loading" />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          Failed to load Analytics V2: {errorMessage}
        </AlertDescription>
      </Alert>
    );
  }

  if (!embedUrl) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          No embed URL generated. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box id="omni-embed-container" w="100%">
      {embedContext && (
        <Flex
          mb={2}
          fontSize="sm"
          color="gray.500"
          gap={4}
          wrap="wrap"
          data-testid="omni-embed-debug"
        >
          <Flex gap={1}>
            <Text>Entity:</Text>
            <Code>{embedContext.entityKey}</Code>
          </Flex>
          <Flex gap={1}>
            <Text>analyticsClientId:</Text>
            <Code>
              {embedContext.analyticsClientId ?? 'unset (fail-closed)'}
            </Code>
          </Flex>
          <Flex gap={1}>
            <Text>Env:</Text>
            <Code>{embedContext.environment}</Code>
          </Flex>
          <Flex gap={1}>
            <Text>Path:</Text>
            <Code>{embedContext.path}</Code>
          </Flex>
          <Flex gap={1}>
            <Text>Folder:</Text>
            <Code>{embedContext.folderLabel}</Code>
            <Code>/f/{embedContext.folderPath}</Code>
          </Flex>
          {embedContext.path === '/' && (
            <Text data-testid="omni-embed-folder-hint">
              Opens on the content browser home, not the folder. Set
              analyticsDefaultPath to the folder path above to open it.
            </Text>
          )}
        </Flex>
      )}
      <Box h={height}>
        <iframe
          // Key forces iframe to remount with fresh URL (one-time secrets can't be reused)
          key={embedUrl}
          src={embedUrl}
          title="Analytics V2"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '8px',
          }}
          allow="clipboard-write"
        />
      </Box>
    </Box>
  );
};
